import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppToast } from '@/hooks/useAppToast';
import { TENANT_ROUTES } from '@/constants/routes';
import { createConsent } from '@/services/main/consent';
import { getApiErrorMessage } from '@/utils/apiError';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import { invalidateVisitClinicScheduleForPatient } from './useVisitClinic';

function invalidateConsentListForPatient(
  queryClient: ReturnType<typeof useQueryClient>,
  tenant: string,
  clinicNumber: string,
  patientId: string
) {
  return queryClient.invalidateQueries({
    queryKey: ['consents', 'list', tenant, clinicNumber, patientId],
    refetchType: 'active',
  });
}

export type ConsentCreateNavigationState = {
  consentCreateSuccess?: boolean;
};

interface UseCreateConsentMutationProps {
  patientId: string;
}

export function useCreateConsentMutation({ patientId }: UseCreateConsentMutationProps) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenant: tenantParam } = useParams();
  const toast = useAppToast();

  return useMutation({
    mutationFn: async (data: unknown) => {
      const res = await createConsent(tenant, clinicNumber, patientId, data, serviceType);
      // Some endpoints return 200 with success:false + field errors.
      // Normalize to an error so caller-level handlers (422 mapping) can run.
      if (!res?.success) {
        const err: any = new Error(res?.message || '登録ができませんでした。');
        err.response = { status: 422, data: res };
        throw err;
      }
      return res;
    },
    onSuccess: (response) => {
      toast.success('同意書を登録しました。');
      invalidateConsentListForPatient(queryClient, tenant, clinicNumber, patientId);
      void invalidateVisitClinicScheduleForPatient(queryClient, patientId);
      const consentId = response?.data?.consent && typeof response.data.consent === 'object'
        ? (response.data.consent as { id?: number }).id
        : undefined;
      const routeTenant = tenantParam || `${tenant}/${clinicNumber}`;
      authorizeNavigation();
      if (consentId) {
        navigate(TENANT_ROUTES.CONSENTS.EDIT(routeTenant, patientId, consentId), {
          state: { consentCreateSuccess: true } satisfies ConsentCreateNavigationState,
        });
      } else {
        navigate(TENANT_ROUTES.CONSENTS.LIST(routeTenant, patientId), {
          state: { consentCreateSuccess: true } satisfies ConsentCreateNavigationState,
        });
      }
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 422) {
        return;
      }
      toast.error(getApiErrorMessage(error, '更新ができませんでした。'));
    },
  });
}
